/**
 * useRevokeDeploymentToken Hook
 *
 * React Query mutation for revoking a deployment token.
 */

import { useMutation, useQueryClient } from '@tanstack/react-query';
import type { UseMutationResult } from '@tanstack/react-query';
import { deploymentTokenService } from '../../../api/deployment-token.service';
import type { DeploymentTokenSanitized } from '../../../types/deployment-token.types';
import { getErrorMessage } from '../../../lib/utils/errors';

interface UseRevokeDeploymentTokenOptions {
  tenantId: string;
  applicationId: string;
  onSuccess?: (data: DeploymentTokenSanitized) => void;
  onError?: (error: Error) => void;
}

/**
 * Hook for revoking a deployment token
 */
export const useRevokeDeploymentToken = (
  options: UseRevokeDeploymentTokenOptions
): UseMutationResult<DeploymentTokenSanitized, Error, string> => {
  const { tenantId, applicationId, onSuccess, onError } = options;
  const queryClient = useQueryClient();

  return useMutation<DeploymentTokenSanitized, Error, string>({
    mutationFn: async (tokenId: string) => {
      console.log('[useRevokeDeploymentToken] Revoking deployment token:', tokenId);
      return await deploymentTokenService.revokeDeploymentToken(tenantId, applicationId, tokenId);
    },
    onSuccess: (data) => {
      console.log('[useRevokeDeploymentToken] Deployment token revoked successfully:', data.tokenId);
      // Invalidate deployment tokens list query
      queryClient.invalidateQueries({ queryKey: ['deployment-tokens', tenantId, applicationId] });
      onSuccess?.(data);
    },
    onError: (error) => {
      console.error('[useRevokeDeploymentToken] Failed to revoke deployment token:', getErrorMessage(error));
      onError?.(error);
    },
  });
};
